import { Download, ArrowUpRight } from "lucide-react";
import { motion } from "framer-motion";

function whatsappLink(number, productName, brand = "B'Premium") {
  const n = (number || "").replace(/[^0-9]/g, "");
  const text = encodeURIComponent(
    `Hi ${brand}, I'd like to enquire about "${productName}". Please share details.`
  );
  return `https://wa.me/${n}?text=${text}`;
}

function downloadPdf(base64, name) {
  if (!base64) return;
  // If it's already a data URL / http URL, just open it
  if (base64.startsWith("http") || base64.startsWith("data:")) {
    const a = document.createElement("a");
    a.href = base64;
    a.download = `${name}.pdf`;
    a.target = "_blank";
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
    return;
  }
  // raw base64
  const link = `data:application/pdf;base64,${base64}`;
  const a = document.createElement("a");
  a.href = link;
  a.download = `${name}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
}

export default function ProductCard({
  product,
  whatsappNumber,
  compact = false,
  index = 0,
}) {
  const {
    name,
    category = "BABUTEX",
    image_base64,
    pdf_base64,
  } = product;

  return (
    <motion.article
      data-testid="catalog-product-card"
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.7, delay: (index % 6) * 0.05, ease: [0.22, 1, 0.36, 1] }}
      className="group relative aspect-square overflow-hidden border border-white/10 bg-navy-800"
    >
      {/* image */}
      <img
        src={image_base64}
        alt={name}
        loading="lazy"
        className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.06]"
      />

      {/* spotlight vignette */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,rgba(5,8,20,0.35)_55%,rgba(5,8,20,0.85)_100%)]" />

      {/* bottom gradient */}
      <div className="absolute inset-0 bg-gradient-to-t from-navy/95 via-navy/25 to-transparent" />

      {/* category tag */}
      <span
        data-testid="product-category-pill"
        className={`absolute ${
          compact ? "top-2 left-2 text-[9px] px-2 py-0.5" : "top-4 left-4 text-[10px] px-3 py-1"
        } bg-white/10 backdrop-blur-md text-white rounded-full tracking-[0.2em] font-semibold uppercase border border-white/15`}
      >
        {category}
      </span>

      {/* bottom content */}
      <div
        className={`absolute inset-x-0 bottom-0 ${
          compact ? "p-3" : "p-4 md:p-5"
        }`}
      >
        <h3
          className={`text-white font-display ${
            compact ? "text-sm mb-2" : "text-lg md:text-xl mb-3"
          } leading-tight underline decoration-white/30 decoration-1 underline-offset-[3px]`}
        >
          {name}
        </h3>

        <div className="flex items-center gap-2">
          <a
            data-testid="enquire-whatsapp-btn"
            href={whatsappLink(whatsappNumber, name)}
            target="_blank"
            rel="noreferrer"
            className={`inline-flex items-center justify-center gap-1 ${
              compact
                ? "px-2 py-1 text-[8px]"
                : "px-2.5 py-1 text-[9px] md:px-3 md:py-1.5 md:text-[10px]"
            } rounded-full bg-gold text-navy tracking-[0.12em] font-bold uppercase hover:bg-white transition-colors`}
          >
            Enquire{compact ? "" : " Now"}
            <ArrowUpRight size={compact ? 9 : 11} strokeWidth={2.5} />
          </a>

          <button
            data-testid="download-pdf-btn"
            onClick={() => downloadPdf(pdf_base64, name)}
            disabled={!pdf_base64}
            className={`${
              compact ? "w-6 h-6" : "w-7 h-7 md:w-8 md:h-8"
            } shrink-0 rounded-md bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center text-white hover:bg-white hover:text-navy transition-colors disabled:opacity-30 disabled:cursor-not-allowed`}
            aria-label="Download PDF catalog"
          >
            <Download size={compact ? 10 : 12} />
          </button>
        </div>
      </div>
    </motion.article>
  );
}
